
  import {
    defineComponent,
    ref,
    onMounted,
    computed,
    onBeforeUnmount,
  } from 'vue';
  import { useRouter } from 'vue-router';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { Actions } from '@/store/enums/StoreEnums';
  import { useAbility } from '@casl/vue';
  import Datatable from '@/components/kt-datatable/KTDatatable.vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import UsersAdvancedSearch from '@/components/search/UsersAdvancedSearch.vue';

  export default defineComponent({
    name: 'users-listing',
    components: {
      Datatable,
      UsersAdvancedSearch,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const { can } = useAbility();

      const translate = (text) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('usersListing'), [
          translate('members'),
        ]);
      });

      onBeforeUnmount(() => {
        store.dispatch(Actions.RESET_USERS_STORE);
      });

      await store.dispatch(Actions.GET_USERS);
      const loading = ref(false);
      const tableHeader = ref([
        {
          name: translate('mcpAccess'),
          key: 'enable_mcp',
          sortable: false,
        },
        {
          name: translate('id'),
          key: 'id',
          sortable: true,
        },
        {
          name: translate('name'),
          key: 'name',
          sortable: true,
        },
        {
          name: translate('phoneNumber'),
          key: 'phone',
          sortable: true,
        },
        {
          name: translate('emailAddress'),
          key: 'email',
          sortable: true,
        },
        {
          name: translate('accountType'),
          key: 'memberTypeTitle',
          sortable: true,
        },
        {
          name: translate('createdDate'),
          key: 'createdAt',
          sortable: true,
        },
        {
          name: translate('status'),
          key: 'is_active',
          sortable: false,
        },
        {
          name: translate('actions'),
          key: 'actions',
        },
      ]);
      const toggleStatusUser = async (user) => {
        const deletedMessage = user.isActive
          ? 'confirmInActive'
          : 'confirmActive';
        const result = await Swal.fire({
          title: translate('areYouSureQ'),
          icon: 'warning',
          text: translate(deletedMessage),
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: translate('confirm'),
          cancelButtonText: translate('cancel'),
          customClass: {
            confirmButton: 'btn fw-bold btn-warning',
            cancelButton: 'btn btn-light',
          },
        });

        if (result.value) {
          loading.value = true;
          await store.dispatch(Actions.UPDATE_USER, user.id);
          await store.dispatch(Actions.GET_USERS);
          loading.value = false;
          const [errorName] = Object.keys(store.getters.getErrors);
          const error = store.getters.getErrors[errorName];
          if (error) {
            Swal.fire({
              text: translate(error[0]),
              icon: 'error',
              buttonsStyling: false,
              confirmButtonText: translate('tryAgainExcl'),
              customClass: {
                confirmButton: 'btn fw-bold btn-light-danger',
              },
            });
          } else {
            const successMsg = user.isActive
              ? 'SUCCESSFULLY_INACTIVATED'
              : 'SUCCESSFULLY_ACTIVEATED';
            Swal.fire({
              text: translate(successMsg),
              icon: 'success',
              buttonsStyling: false,
              confirmButtonText: translate('ok'),
              customClass: {
                confirmButton: 'btn btn-light-primary',
              },
            });
          }
        }
      };

      const toggleMcpUser = async (user) => {
        const enable = !user.enable_mcp;
        const result = await Swal.fire({
          title: translate('areYouSureQ'),
          icon: 'warning',
          text: translate(enable ? 'confirmEnableMcp' : 'confirmDisableMcp'),
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: translate('confirm'),
          cancelButtonText: translate('cancel'),
          customClass: {
            confirmButton: 'btn fw-bold btn-warning',
            cancelButton: 'btn btn-light',
          },
        });
        if (!result.value) return;

        loading.value = true;
        await store.dispatch(Actions.UPDATE_USER_MCP, {
          id: user.id,
          enable_mcp: enable,
        });
        await store.dispatch(Actions.GET_USERS);
        loading.value = false;
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];
        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
        } else {
          Swal.fire({
            text: translate(enable ? 'MCP_ENABLED' : 'MCP_DISABLED'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          });
        }
      };

      const onFilterChange = async () => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_USER_CURRENT_PAGE, 1);
        await store.dispatch(Actions.GET_USERS);
        loading.value = false;
      };

      const onResetFilters = async () => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_USER_CURRENT_PAGE, 1);
        await store.dispatch(Actions.GET_USERS);
        loading.value = false;
      };

      const goTo = (pageName) => {
        router.push({ name: pageName });
      };

      const pageChanged = async (val) => {
        loading.value = true;
        await store.dispatch(Actions.UPDATE_USER_CURRENT_PAGE, val);
        await store.dispatch(Actions.GET_USERS);
        loading.value = false;
      };

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        tableData: computed(() => store.getters.usersList),
        tableHeader,
        toggleStatusUser,
        toggleMcpUser,
        translate,
        totalItems: computed(() => store.getters.listUsersCount),
        pageChanged,
        goTo,
        loading,
        can,
        onFilterChange,
        onResetFilters,
      };
    },
  });
